import {token} from "@atlaskit/tokens";
import type {ReactNode} from "react";
import {MODULE_STATUS_BAR_COLORS, ModuleStatusBarsIcon, type ModuleStatusBarColor} from "~/components/icons/moduleStatusBarsIcon";
import type {OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import type {KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import {LIST_INQUEUE_ASSIGNMENT_STATUS} from "~/components/ottopilotDataViewer/listStatusConfig";
import type {SidebarMenuConfigItem, SidebarMenuConfigNestedItem} from "~/components/pageScaffolds/workspacePageScaffold/constants";
import {UserStatus} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {userStatusToDisplayName} from "~/utilities/userStatusToDisplayName";
import {
    EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EmotoradCallStatusEnum,
    EmotoradCustomerStatusEnum,
    EmotoradEventTypes,
    EmotoradEventContainerStatusEnum,
    EmotoradLeadEventStatusEnum,
    EmotoradSalesStatusEnum,
    emotoradCallStatuses,
    emotoradCustomerStatuses,
    emotoradEventContainerStatuses,
    emotoradLeadEventStatuses,
    emotoradSalesStatuses,
} from "~/domainModels/workspaceSpecific/emotorad";
import {
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AmaronCallStatusEnum,
    AmaronEnquiryStatusEnum,
    AmaronEventTypes,
    AmaronLeadStatusEnum,
    AmaronServiceCategoryEnum,
    amaronCallStatuses,
    amaronEnquiryStatuses,
    amaronLeadStatuses,
    amaronServiceCategories,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AppointmentStatusEnum,
    CallStatusEnum,
    DuluxEventTypes,
    EnquiryStatusEnum,
    DuluxLeadStatusEnum,
    OnboardingStatusEnum,
    appointmentStatuses,
    callStatuses,
    enquiryStatuses,
    leadStatuses,
    onboardingStatuses,
} from "~/domainModels/workspaceSpecific/dulux";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {getAmaronStatusNavIconColor} from "~/domainModels/amaronCalendarStatusColors";

export type {ModuleStatusBarColor};

export type ModuleStatusNavConfig = {
    statusColumnId: string;
    statuses: Array<{id: string; displayName: string}>;
};

function isSidebarLinkItem(item: SidebarMenuConfigItem): item is Extract<SidebarMenuConfigItem, {type: "linkItem"}> {
    return item.type === "linkItem";
}

/** Statuses omitted from Emotorad sidebar module-status children (still reachable via table/filters). */
const EMOTORAD_SIDEBAR_EXCLUDED_STATUS_IDS = new Set<string>([
    EmotoradEventContainerStatusEnum.DUPLICATE,
    EmotoradEventContainerStatusEnum.ARCHIVE,
    EmotoradLeadEventStatusEnum.DUPLICATE,
    EmotoradLeadEventStatusEnum.ARCHIVE,
    EmotoradCallStatusEnum.ARCHIVE,
    EmotoradSalesStatusEnum.ARCHIVE,
]);

function filterStatusesForEmotoradSidebarNav(statuses: Array<{id: string; displayName: string}>): Array<{id: string; displayName: string}> {
    return statuses.filter((status) => EMOTORAD_SIDEBAR_EXCLUDED_STATUS_IDS.has(String(status.id)) !== true);
}

/** L2-derived disposition tracker labels (frontend-filtered `status` column). */
export const EMOTORAD_DISPOSITION_TRACKER_L2_STATUS = {
    callBackLater: "Call-Back Later",
    invalid: "Invalid",
    futureIntent: "Future-Intent",
    highIntent: "High-intent",
    lost: "Lost",
} as const;

export const EMOTORAD_DISPOSITION_TRACKER_FRONTEND_STATUSES = [
    EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.callBackLater,
    EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.invalid,
    EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.futureIntent,
    EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.highIntent,
    EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.lost,
] as const;

const EMOTORAD_STATUS_NAV_ICON_BY_STATUS_ID: Record<string, ModuleStatusBarColor> = {
    [EmotoradEventContainerStatusEnum.LIVE]: "active",
    [EmotoradEventContainerStatusEnum.CONVERTED]: "assigned",
    [EmotoradEventContainerStatusEnum.CHURNED]: "inactive",
    [EmotoradEventContainerStatusEnum.DUPLICATE]: "unassigned",
    [EmotoradEventContainerStatusEnum.ARCHIVE]: "inactive",
    [EmotoradLeadEventStatusEnum.OPEN]: "unassigned",
    [EmotoradLeadEventStatusEnum.ENGAGED]: "active",
    [EmotoradLeadEventStatusEnum.NON_ENGAGED]: "assigned",
    [EmotoradLeadEventStatusEnum.DUPLICATE]: "unassigned",
    [EmotoradLeadEventStatusEnum.ARCHIVE]: "inactive",
    [EmotoradCallStatusEnum.SCHEDULED]: "active",
    [EmotoradCallStatusEnum.LOGGED]: "assigned",
    [EmotoradCallStatusEnum.CANCELLED]: "inactive",
    [EmotoradCallStatusEnum.ARCHIVE]: "inactive",
    [EmotoradSalesStatusEnum.UNVERIFIED]: "unassigned",
    [EmotoradSalesStatusEnum.VERIFIED]: "assigned",
    [EmotoradSalesStatusEnum.ARCHIVE]: "inactive",
    [EmotoradCustomerStatusEnum.ACTIVE]: "active",
    [EmotoradCustomerStatusEnum.INACTIVE]: "inactive",
    assigned: "assigned",
    unassigned: "unassigned",
    [UserStatus.active]: "active",
    [UserStatus.suspended]: "inactive",
    [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.callBackLater]: "assigned",
    [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.invalid]: "inactive",
    [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.futureIntent]: "active",
    [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.highIntent]: "active",
    [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.lost]: "unassigned",
};

export function getEmotoradStatusNavIconColor(statusId: string): string {
    const variant = EMOTORAD_STATUS_NAV_ICON_BY_STATUS_ID[String(statusId)];
    if (variant === undefined) {
        return MODULE_STATUS_BAR_COLORS.active;
    }
    return MODULE_STATUS_BAR_COLORS[variant];
}

export function getEmotoradJourneyLifecycleStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
        statuses: filterStatusesForEmotoradSidebarNav(Object.values(emotoradEventContainerStatuses)),
    };
}

export function getEmotoradEventTemplateStatusNavConfig(eventTemplateId: Uuid): ModuleStatusNavConfig | null {
    switch (String(eventTemplateId)) {
        case String(EmotoradEventTypes.lead):
            return {
                statusColumnId: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
                statuses: filterStatusesForEmotoradSidebarNav(Object.values(emotoradLeadEventStatuses)),
            };
        case String(EmotoradEventTypes.call):
            return {
                statusColumnId: EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
                statuses: filterStatusesForEmotoradSidebarNav(Object.values(emotoradCallStatuses)),
            };
        case String(EmotoradEventTypes.walkin):
            return {
                statusColumnId: EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
                statuses: filterStatusesForEmotoradSidebarNav(Object.values(emotoradCallStatuses)),
            };
        case String(EmotoradEventTypes.testride):
            return {
                statusColumnId: EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
                statuses: filterStatusesForEmotoradSidebarNav(Object.values(emotoradCallStatuses)),
            };
        case String(EmotoradEventTypes.sales):
            return {
                statusColumnId: EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
                statuses: filterStatusesForEmotoradSidebarNav(Object.values(emotoradSalesStatuses)),
            };
        default:
            return null;
    }
}

export function getEmotoradCustomersStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: EMOTORAD__CUSTOMERS__CUSTOMER_STATUS_COLUMN_ID,
        statuses: filterStatusesForEmotoradSidebarNav(Object.values(emotoradCustomerStatuses)),
    };
}

/**
 * Sidebar uses Active/Inactive ids; the customer status column often stores journey lifecycle UUIDs.
 * @see formatEmotoradCustomerStatusCell
 */
export function resolveEmotoradCustomerStatusValuesForModuleNavStatusId(statusId: string): ReadonlyArray<string> {
    if (statusId === String(EmotoradCustomerStatusEnum.ACTIVE)) {
        return [String(EmotoradCustomerStatusEnum.ACTIVE), String(EmotoradEventContainerStatusEnum.LIVE)];
    }
    if (statusId === String(EmotoradCustomerStatusEnum.INACTIVE)) {
        return [
            String(EmotoradCustomerStatusEnum.INACTIVE),
            String(EmotoradEventContainerStatusEnum.CONVERTED),
            String(EmotoradEventContainerStatusEnum.CHURNED),
            String(EmotoradEventContainerStatusEnum.DUPLICATE),
            String(EmotoradEventContainerStatusEnum.ARCHIVE),
        ];
    }
    return [statusId];
}

/** In Queue assignment status (frontend-filtered; not a journey UUID column). */
export function getEmotoradInQueueStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: "status",
        statuses: [
            {id: "assigned", displayName: "Assigned"},
            {id: "unassigned", displayName: "Unassigned"},
        ],
    };
}

export function getEmotoradMySalesTeamStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: "status",
        statuses: [
            {
                id: UserStatus.active,
                displayName: userStatusToDisplayName(UserStatus.active),
            },
            {
                id: UserStatus.suspended,
                displayName: userStatusToDisplayName(UserStatus.suspended),
            },
            {
                id: UserStatus.deleted,
                displayName: userStatusToDisplayName(UserStatus.deleted),
            },
        ],
    };
}

export function getEmotoradDispositionTrackerStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: "status",
        statuses: EMOTORAD_DISPOSITION_TRACKER_FRONTEND_STATUSES.map((status) => ({
            id: status,
            displayName: status,
        })),
    };
}

export function getEmotoradDispositionTrackerKanbanColumnConfig(): Array<KanbanBoardColumnConfig> {
    return [
        {
            id: uniqueUuid`disposition-tracker-kanban-status-call-back-later`,
            displayName: EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.callBackLater,
            background: token("color.background.accent.blue.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.callBackLater],
        },
        {
            id: uniqueUuid`disposition-tracker-kanban-status-invalid`,
            displayName: EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.invalid,
            background: token("color.background.accent.gray.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.invalid],
        },
        {
            id: uniqueUuid`disposition-tracker-kanban-status-future-intent`,
            displayName: EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.futureIntent,
            background: token("color.background.accent.yellow.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.futureIntent],
        },
        {
            id: uniqueUuid`disposition-tracker-kanban-status-high-intent`,
            displayName: EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.highIntent,
            background: token("color.background.accent.lime.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.highIntent],
        },
        {
            id: uniqueUuid`disposition-tracker-kanban-status-lost`,
            displayName: EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.lost,
            background: token("color.background.accent.red.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [EMOTORAD_DISPOSITION_TRACKER_L2_STATUS.lost],
        },
    ];
}

export function getEmotoradCustomersKanbanColumnConfig(): Array<KanbanBoardColumnConfig> {
    return [
        {
            id: EmotoradCustomerStatusEnum.ACTIVE as Uuid,
            displayName: emotoradCustomerStatuses[EmotoradCustomerStatusEnum.ACTIVE].displayName,
            background: token("color.background.accent.green.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [EmotoradCustomerStatusEnum.ACTIVE, EmotoradEventContainerStatusEnum.LIVE],
        },
        {
            id: EmotoradCustomerStatusEnum.INACTIVE as Uuid,
            displayName: emotoradCustomerStatuses[EmotoradCustomerStatusEnum.INACTIVE].displayName,
            background: token("color.background.accent.red.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [
                EmotoradCustomerStatusEnum.INACTIVE,
                EmotoradEventContainerStatusEnum.CONVERTED,
                EmotoradEventContainerStatusEnum.CHURNED,
                EmotoradEventContainerStatusEnum.DUPLICATE,
                EmotoradEventContainerStatusEnum.ARCHIVE,
            ],
        },
    ];
}

export function getEmotoradInQueueKanbanColumnConfig(): Array<KanbanBoardColumnConfig> {
    return [
        {
            id: uniqueUuid`75a844f6-4306-435b-b6f3-c72d20084f71`,
            displayName: "Assigned",
            background: token("color.background.accent.green.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [LIST_INQUEUE_ASSIGNMENT_STATUS.assigned],
        },
        {
            id: uniqueUuid`3f2e2eb7-8f74-4aa1-a6e1-ff4f87be5e95`,
            displayName: "Unassigned",
            background: token("color.background.accent.yellow.subtlest"),
            textColor: token("color.text"),
            headerBorderRadius: "4px 4px 0 0",
            headerBorderColor: token("color.border"),
            headerBorderWidth: "0.5px",
            possibleValues: [LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned, ""],
        },
    ];
}

export {
    getAmaronInQueueKanbanColumnConfig,
    getAmaronJourneyLeadsKanbanColumnConfig,
    getAmaronStatusNavIconColor,
} from "~/domainModels/amaronCalendarStatusColors";

/** Synthetic field on in-queue journey rows (see journeys route `EMOTORAD_INQUEUE_STATUS_FIELD`). */
export const EMOTORAD_INQUEUE_KANBAN_STATUS_FIELD = "__inQueueStatus";

/** Synthetic field on Amaron in-queue journey rows (see journeys route). */
export const AMARON_INQUEUE_KANBAN_STATUS_FIELD = "__amaronInQueueStatus";

function kanbanColumnMatchesModuleStatusId(column: KanbanBoardColumnConfig, statusId: string): boolean {
    if (column.possibleValues.some((value) => String(value) === statusId) === true) {
        return true;
    }
    if (statusId === "assigned" && column.possibleValues.some((value) => String(value) === LIST_INQUEUE_ASSIGNMENT_STATUS.assigned) === true) {
        return true;
    }
    if (
        statusId === "unassigned" &&
        column.possibleValues.some((value) => String(value) === LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned || String(value) === "") === true
    ) {
        return true;
    }
    if (statusId === String(EmotoradCustomerStatusEnum.ACTIVE)) {
        return column.possibleValues.some((value) => String(value) === String(EmotoradCustomerStatusEnum.ACTIVE) || String(value) === String(EmotoradEventContainerStatusEnum.LIVE));
    }
    if (statusId === String(EmotoradCustomerStatusEnum.INACTIVE)) {
        return column.possibleValues.some(
            (value) =>
                String(value) === String(EmotoradCustomerStatusEnum.INACTIVE) ||
                String(value) === String(EmotoradEventContainerStatusEnum.CONVERTED) ||
                String(value) === String(EmotoradEventContainerStatusEnum.CHURNED) ||
                String(value) === String(EmotoradEventContainerStatusEnum.DUPLICATE) ||
                String(value) === String(EmotoradEventContainerStatusEnum.ARCHIVE),
        );
    }
    return false;
}

/** When sidebar status navigation is active, show only the matching kanban column (avoids empty sibling columns). */
export function filterKanbanColumnsForModuleStatusId(
    columnConfig: Array<KanbanBoardColumnConfig>,
    statusId: string | undefined,
): Array<KanbanBoardColumnConfig> {
    if (statusId === undefined || statusId.trim() === "") {
        return columnConfig;
    }
    const matchingColumns = columnConfig.filter((column) => kanbanColumnMatchesModuleStatusId(column, statusId));
    return matchingColumns.length > 0 ? matchingColumns : columnConfig;
}

export function buildModuleStatusHref(args: {basePath: string; statusId?: string}): string {
    const [path] = args.basePath.split("?");
    const params = new URLSearchParams();
    params.set("view", "table");
    if (args.statusId !== undefined) {
        params.set("statusId", String(args.statusId));
    }
    return `${path}?${params.toString()}`;
}

export function buildEmotoradModuleStatusSidebarChildren(args: {
    basePath: string;
    parentUrlPattern: RegExp;
    statusNavConfig: ModuleStatusNavConfig;
}): SidebarMenuConfigNestedItem[] {
    return args.statusNavConfig.statuses.map((status) => {
        const escapedStatusId = String(status.id).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return {
            id: `emotorad-status-nav-${status.id}` as Uuid,
            type: "linkItem" as const,
            href: buildModuleStatusHref({basePath: args.basePath, statusId: status.id}),
            children: status.displayName,
            elemBefore: (
                <ModuleStatusBarsIcon
                    color={getEmotoradStatusNavIconColor(status.id)}
                    label={status.displayName}
                />
            ) as ReactNode,
            urlPattern: new RegExp(`\\bstatusId=${escapedStatusId}\\b`),
        };
    });
}

export function getEmotoradModuleStatusNavValidStatusIds(statusNavConfig: ModuleStatusNavConfig | null): ReadonlySet<string> | undefined {
    if (statusNavConfig === null) {
        return undefined;
    }
    return new Set(statusNavConfig.statuses.map((status) => String(status.id)));
}

export function getEmotoradEventTemplateStatusNestedSidebarItems(item: SidebarMenuConfigNestedItem): SidebarMenuConfigNestedItem[] {
    if (item.type !== "linkItem") {
        return [];
    }
    const statusNavConfig = getEmotoradEventTemplateStatusNavConfig(item.id as Uuid);
    if (statusNavConfig === null) {
        return [];
    }
    const basePath = item.href.split("?")[0] ?? item.href;
    return buildEmotoradModuleStatusSidebarChildren({
        basePath,
        parentUrlPattern: item.urlPattern ?? /^$/,
        statusNavConfig,
    });
}

export function applyEmotoradStatusNavToSidebarItem(item: SidebarMenuConfigItem, statusNavConfig: ModuleStatusNavConfig): SidebarMenuConfigItem {
    if (isSidebarLinkItem(item) !== true) {
        return item;
    }
    const basePath = item.href.split("?")[0] ?? item.href;
    const nestedItems = buildEmotoradModuleStatusSidebarChildren({
        basePath,
        parentUrlPattern: item.urlPattern ?? /^$/,
        statusNavConfig,
    });
    if (nestedItems.length === 0) {
        return item;
    }
    return {
        ...item,
        href: buildModuleStatusHref({basePath}),
        nestType: "collapsibleList",
        nestedItems,
    };
}

/** Statuses omitted from Amaron sidebar module-status children (still reachable via table/filters). */
const AMARON_SIDEBAR_EXCLUDED_STATUS_IDS = new Set<string>([
    String(AmaronLeadStatusEnum.ARCHIVED),
    String(AmaronEnquiryStatusEnum.ARCHIVE),
    String(AmaronEnquiryStatusEnum.DUPLICATE),
    String(AmaronCallStatusEnum.ARCHIVE),
    String(AmaronServiceCategoryEnum.ARCHIVE),
]);

function filterStatusesForAmaronSidebarNav(statuses: Array<{id: string; displayName: string}>): Array<{id: string; displayName: string}> {
    return statuses.filter((status) => AMARON_SIDEBAR_EXCLUDED_STATUS_IDS.has(String(status.id)) !== true);
}

export function getAmaronJourneyLeadsStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        statuses: filterStatusesForAmaronSidebarNav(Object.values(amaronLeadStatuses)),
    };
}

export function getAmaronInQueueStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: AMARON_INQUEUE_KANBAN_STATUS_FIELD,
        statuses: [
            {id: "assigned", displayName: "Assigned"},
            {id: "unassigned", displayName: "Unassigned"},
        ],
    };
}

export function getAmaronEventTemplateStatusNavConfig(eventTemplateId: Uuid): ModuleStatusNavConfig | null {
    switch (String(eventTemplateId)) {
        case String(AmaronEventTypes.Enquiry):
            return {
                statusColumnId: AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
                statuses: filterStatusesForAmaronSidebarNav(Object.values(amaronEnquiryStatuses)),
            };
        case String(AmaronEventTypes.Calls):
            return {
                statusColumnId: AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                statuses: filterStatusesForAmaronSidebarNav(Object.values(amaronCallStatuses)),
            };
        case String(AmaronEventTypes.Service):
            return {
                statusColumnId: AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
                statuses: filterStatusesForAmaronSidebarNav(Object.values(amaronServiceCategories)),
            };
        default:
            return null;
    }
}

export function buildAmaronModuleStatusSidebarChildren(args: {basePath: string; statusNavConfig: ModuleStatusNavConfig}): SidebarMenuConfigNestedItem[] {
    return args.statusNavConfig.statuses.map((status) => {
        const escapedStatusId = String(status.id).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return {
            id: `amaron-status-nav-${status.id}` as Uuid,
            type: "linkItem" as const,
            href: buildModuleStatusHref({basePath: args.basePath, statusId: status.id}),
            children: status.displayName,
            elemBefore: (
                <ModuleStatusBarsIcon
                    color={getAmaronStatusNavIconColor(status.id)}
                    label={status.displayName}
                />
            ) as ReactNode,
            urlPattern: new RegExp(`\\bstatusId=${escapedStatusId}\\b`),
        };
    });
}

export function getAmaronModuleStatusNavValidStatusIds(statusNavConfig: ModuleStatusNavConfig | null): ReadonlySet<string> | undefined {
    if (statusNavConfig === null) {
        return undefined;
    }
    return new Set(statusNavConfig.statuses.map((status) => String(status.id)));
}

/** True when the sidebar has navigated into a status child view (not the parent module). */
export function isAmaronModuleStatusChildView(args: {statusNavConfig: ModuleStatusNavConfig; activeStatusId: string | null}): boolean {
    if (args.activeStatusId === null) {
        return false;
    }
    const sidebarStatusIds = args.statusNavConfig.statuses.map((status) => String(status.id));
    return sidebarStatusIds.includes(args.activeStatusId) === true;
}

/** Column ids that represent module status in the filter toolbar (In Queue has multiple aliases). */
export function getAmaronModuleStatusFilterColumnIds(statusNavConfig: ModuleStatusNavConfig): ReadonlySet<string> {
    const ids = new Set<string>([String(statusNavConfig.statusColumnId)]);
    if (statusNavConfig.statusColumnId === AMARON_INQUEUE_KANBAN_STATUS_FIELD) {
        ids.add(String(AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID));
        ids.add("status");
    }
    return ids;
}

export type ModuleStatusFilterScaffold = {
    isModuleStatusChildView: (args: {
        statusNavConfig: ModuleStatusNavConfig;
        activeStatusId: string | null;
    }) => boolean;
    getModuleStatusFilterColumnIds: (statusNavConfig: ModuleStatusNavConfig) => ReadonlySet<string>;
};

export const AMARON_MODULE_STATUS_FILTER_SCAFFOLD: ModuleStatusFilterScaffold = {
    isModuleStatusChildView: isAmaronModuleStatusChildView,
    getModuleStatusFilterColumnIds: getAmaronModuleStatusFilterColumnIds,
};

/** Parent module views expose status filtering; sidebar status children hide it (URL still scopes data). */
export function applyModuleStatusFilterVisibilityToColumns<T>(
    columns: Array<OttopilotColumn<T>>,
    args: {
        statusNavConfig: ModuleStatusNavConfig;
        activeStatusId: string | null;
        moduleStatusFilterScaffold: ModuleStatusFilterScaffold;
    },
): Array<OttopilotColumn<T>> {
    if (args.moduleStatusFilterScaffold.isModuleStatusChildView(args) !== true) {
        return columns;
    }
    const hiddenColumnIds = args.moduleStatusFilterScaffold.getModuleStatusFilterColumnIds(
        args.statusNavConfig,
    );
    return columns.map((column) => {
        if (hiddenColumnIds.has(String(column.id)) !== true) {
            return column;
        }
        return {
            ...column,
            filteringCapability_contains: false,
            filteringCapability_in: undefined,
            customFilter: undefined,
        };
    });
}

export function applyAmaronModuleStatusFilterVisibilityToColumns<T>(
    columns: Array<OttopilotColumn<T>>,
    args: {
        statusNavConfig: ModuleStatusNavConfig;
        activeStatusId: string | null;
    },
): Array<OttopilotColumn<T>> {
    return applyModuleStatusFilterVisibilityToColumns(columns, {
        ...args,
        moduleStatusFilterScaffold: AMARON_MODULE_STATUS_FILTER_SCAFFOLD,
    });
}

/** Sidebar status slice → filter picker options (parent view only; child views hide the status filter). */
export function resolveModuleStatusFilterPickerValues(args: {statusNavConfig: ModuleStatusNavConfig; activeStatusId: string | null}): Array<string> {
    const sidebarStatusIds = args.statusNavConfig.statuses.map((status) => String(status.id));
    return sidebarStatusIds;
}

export function resolveAmaronModuleStatusFilterPickerValues(args: {statusNavConfig: ModuleStatusNavConfig; activeStatusId: string | null}): Array<string> {
    return resolveModuleStatusFilterPickerValues(args);
}

export function getAmaronEventTemplateStatusNestedSidebarItems(item: SidebarMenuConfigNestedItem): SidebarMenuConfigNestedItem[] {
    if (item.type !== "linkItem") {
        return [];
    }
    const statusNavConfig = getAmaronEventTemplateStatusNavConfig(item.id as Uuid);
    if (statusNavConfig === null) {
        return [];
    }
    const basePath = item.href.split("?")[0] ?? item.href;
    return buildAmaronModuleStatusSidebarChildren({
        basePath,
        statusNavConfig,
    });
}

export function applyAmaronStatusNavToSidebarItem(item: SidebarMenuConfigItem, statusNavConfig: ModuleStatusNavConfig): SidebarMenuConfigItem {
    if (isSidebarLinkItem(item) !== true) {
        return item;
    }
    const basePath = item.href.split("?")[0] ?? item.href;
    const nestedItems = buildAmaronModuleStatusSidebarChildren({
        basePath,
        statusNavConfig,
    });
    if (nestedItems.length === 0) {
        return item;
    }
    return {
        ...item,
        href: buildModuleStatusHref({basePath}),
        nestType: "collapsibleList",
        nestedItems,
    };
}

const DULUX_STATUS_NAV_ICON_BY_STATUS_ID: Record<string, ModuleStatusBarColor> = {
    [DuluxLeadStatusEnum.OPEN_LEAD]: "active",
    [DuluxLeadStatusEnum.AID_ONBOARDED]: "assigned",
    [DuluxLeadStatusEnum.CHURNED]: "inactive",
    [DuluxLeadStatusEnum.ARCHIVED]: "inactive",
    [EnquiryStatusEnum.NEW]: "unassigned",
    [EnquiryStatusEnum.DUPLICATE]: "inactive",
    [OnboardingStatusEnum.NOT_ONBOARDED]: "unassigned",
    [OnboardingStatusEnum.ONBOARDED]: "assigned",
    [CallStatusEnum.SCHEDULED]: "active",
    [CallStatusEnum.LOGGED]: "assigned",
    [CallStatusEnum.CANCELLED]: "inactive",
    [AppointmentStatusEnum.SCHEDULED]: "active",
    [AppointmentStatusEnum.LOGGED]: "assigned",
    [AppointmentStatusEnum.CANCELLED]: "inactive",
};

export function getDuluxStatusNavIconColor(statusId: string): string {
    const variant = DULUX_STATUS_NAV_ICON_BY_STATUS_ID[String(statusId)];
    if (variant === undefined) {
        return MODULE_STATUS_BAR_COLORS.active;
    }
    return MODULE_STATUS_BAR_COLORS[variant];
}

/** Statuses omitted from Dulux sidebar module-status children (still reachable via table/filters). */
const DULUX_SIDEBAR_EXCLUDED_STATUS_IDS = new Set<string>([
    String(DuluxLeadStatusEnum.ARCHIVED),
]);

function filterStatusesForDuluxSidebarNav(statuses: Array<{id: string; displayName: string}>): Array<{id: string; displayName: string}> {
    return statuses.filter((status) => DULUX_SIDEBAR_EXCLUDED_STATUS_IDS.has(String(status.id)) !== true);
}

export function getDuluxJourneyLeadsStatusNavConfig(): ModuleStatusNavConfig {
    return {
        statusColumnId: DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        statuses: filterStatusesForDuluxSidebarNav(Object.values(leadStatuses)),
    };
}

export function getDuluxEventTemplateStatusNavConfig(eventTemplateId: Uuid): ModuleStatusNavConfig | null {
    switch (String(eventTemplateId)) {
        case String(DuluxEventTypes.Enquiry):
            return {
                statusColumnId: DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
                statuses: Object.values(enquiryStatuses),
            };
        case String(DuluxEventTypes.Onboarding):
            return {
                statusColumnId: DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
                statuses: Object.values(onboardingStatuses),
            };
        case String(DuluxEventTypes.Calls):
            return {
                statusColumnId: DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                statuses: Object.values(callStatuses),
            };
        case String(DuluxEventTypes.Appointments):
            return {
                statusColumnId: DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
                statuses: Object.values(appointmentStatuses),
            };
        default:
            return null;
    }
}

export function buildDuluxModuleStatusSidebarChildren(args: {basePath: string; statusNavConfig: ModuleStatusNavConfig}): SidebarMenuConfigNestedItem[] {
    return args.statusNavConfig.statuses.map((status) => {
        const escapedStatusId = String(status.id).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return {
            id: `dulux-status-nav-${status.id}` as Uuid,
            type: "linkItem" as const,
            href: buildModuleStatusHref({basePath: args.basePath, statusId: status.id}),
            children: status.displayName,
            elemBefore: (
                <ModuleStatusBarsIcon
                    color={getDuluxStatusNavIconColor(status.id)}
                    label={status.displayName}
                />
            ) as ReactNode,
            urlPattern: new RegExp(`\\bstatusId=${escapedStatusId}\\b`),
        };
    });
}

export function getDuluxModuleStatusNavValidStatusIds(statusNavConfig: ModuleStatusNavConfig | null): ReadonlySet<string> | undefined {
    if (statusNavConfig === null) {
        return undefined;
    }
    return new Set(statusNavConfig.statuses.map((status) => String(status.id)));
}

export function isDuluxModuleStatusChildView(args: {statusNavConfig: ModuleStatusNavConfig; activeStatusId: string | null}): boolean {
    if (args.activeStatusId === null) {
        return false;
    }
    const sidebarStatusIds = args.statusNavConfig.statuses.map((status) => String(status.id));
    return sidebarStatusIds.includes(args.activeStatusId) === true;
}

export function getDuluxModuleStatusFilterColumnIds(statusNavConfig: ModuleStatusNavConfig): ReadonlySet<string> {
    return new Set([String(statusNavConfig.statusColumnId)]);
}

export const DULUX_MODULE_STATUS_FILTER_SCAFFOLD: ModuleStatusFilterScaffold = {
    isModuleStatusChildView: isDuluxModuleStatusChildView,
    getModuleStatusFilterColumnIds: getDuluxModuleStatusFilterColumnIds,
};

export function applyDuluxModuleStatusFilterVisibilityToColumns<T>(
    columns: Array<OttopilotColumn<T>>,
    args: {
        statusNavConfig: ModuleStatusNavConfig;
        activeStatusId: string | null;
    },
): Array<OttopilotColumn<T>> {
    return applyModuleStatusFilterVisibilityToColumns(columns, {
        ...args,
        moduleStatusFilterScaffold: DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
    });
}

export function resolveDuluxModuleStatusFilterPickerValues(args: {statusNavConfig: ModuleStatusNavConfig; activeStatusId: string | null}): Array<string> {
    return resolveModuleStatusFilterPickerValues(args);
}

export function getDuluxEventTemplateStatusNestedSidebarItems(item: SidebarMenuConfigNestedItem): SidebarMenuConfigNestedItem[] {
    if (item.type !== "linkItem") {
        return [];
    }
    const statusNavConfig = getDuluxEventTemplateStatusNavConfig(item.id as Uuid);
    if (statusNavConfig === null) {
        return [];
    }
    const basePath = item.href.split("?")[0] ?? item.href;
    return buildDuluxModuleStatusSidebarChildren({
        basePath,
        statusNavConfig,
    });
}

export function applyDuluxStatusNavToSidebarItem(item: SidebarMenuConfigItem, statusNavConfig: ModuleStatusNavConfig): SidebarMenuConfigItem {
    if (isSidebarLinkItem(item) !== true) {
        return item;
    }
    const basePath = item.href.split("?")[0] ?? item.href;
    const nestedItems = buildDuluxModuleStatusSidebarChildren({
        basePath,
        statusNavConfig,
    });
    if (nestedItems.length === 0) {
        return item;
    }
    return {
        ...item,
        href: buildModuleStatusHref({basePath}),
        nestType: "collapsibleList",
        nestedItems,
    };
}
